import React from 'react'

import NiceModal from '@ebay/nice-modal-react'
import { Button, Divider, Group, Menu, Stack, Tabs } from '@mantine/core'
import { useForm } from '@mantine/form'
import { zodResolver } from 'mantine-form-zod-resolver'
import { z } from 'zod'

import { schemas } from '@showrunner/scrapi'

import { ElementIcon } from '#components/ElementIcon'
import { FancyMenuItem } from '#components/FancyMenuItem'
import { ModalShell, useModalControls } from '#components/Modals'
import { ILoadedScript, useMst } from '#state'
import { BLACKISH_TEXT } from '#theme/colors'
import { getSelectionBlockType, setBlockOverrides } from '#util'
import {
  BlockOverrides,
  blockOverridesParser,
  FormatBlockName,
  isFormatBlockName,
  minifyOverrides,
} from '#util/formats'

import { FontPicker } from './FontPicker'
import { FormattingCheckboxes, StyleCheckboxData } from './FormattingCheckboxes'
import { BulkChangeHandler, changeEditorFontCode } from './helpers'
import { LayoutInputs } from './LayoutInputs'
import { TextColorPicker } from './TextColorPicker'

import styles from './CustomizeStyles.module.scss'

type MenuItem = FormatBlockName | 'script'

const overrideFormValuesSchema = z.object({
  fontCode: schemas.scriptFormats.FontCode,
  blockOverrides: blockOverridesParser,
})

type OverrideFormValues = z.infer<typeof overrideFormValuesSchema>

const gatherColors = ({
  block,
  script,
  overrides,
}: {
  block: MenuItem
  script: ILoadedScript
  overrides: BlockOverrides
}): [string, string, string] => {
  if (!isFormatBlockName(block)) return ['', '', '']

  const currentColor = script.currentFormatValue(block, 'color') ?? ''
  const pendingColor = overrides[block]?.color ?? currentColor
  const originalColor = script.originalFormatValue(block, 'color') ?? ''

  return [currentColor, pendingColor, originalColor]
}

const CustomizeStylesModal = NiceModal.create(
  ({ script }: { script: ILoadedScript }) => {
    const mst = useMst()
    const controls = useModalControls()
    const [activeTab, setActiveTab] = React.useState<string | null>('layout')
    const originalFontCode = script.originalScriptValue('fontCode')

    const form = useForm<OverrideFormValues>({
      initialValues: {
        fontCode: script.fontCode,
        blockOverrides: script.blockOverrides ?? {},
      },
      validate: zodResolver(overrideFormValuesSchema),
    })

    const handleSubmit = form.onSubmit((values) => {
      if (form.isDirty('blockOverrides')) {
        const overrides = minifyOverrides(
          script.originalBlockFormats,
          values.blockOverrides,
        )
        setBlockOverrides(editorView, overrides)
        mst.trackEvent('SCRIPT_BLOCK_FORMATTING_CUSTOMIZED', { overrides })
      }

      if (form.isDirty('fontCode')) {
        changeEditorFontCode({ code: values.fontCode, script })
        mst.trackEvent('SCRIPT_FONT_CUSTOMIZED')
      }

      controls.onClose()
    })

    const { editorState, editorView } = script.pmEditor
    const menuItems = script.observableEditor?.elementPickerKeys ?? []

    const pmSelectionBlockType = getSelectionBlockType(editorState)

    const findMenuItem = (id: string | undefined) =>
      menuItems.find((item) => item.node.name === id)

    const [selectedBlock, setSelectedBlock] = React.useState<MenuItem>(() => {
      const name = findMenuItem(pmSelectionBlockType)?.node.name
      if (name && isFormatBlockName(name)) return name
      return 'script'
    })

    const setValues: BulkChangeHandler = (blockName, changes) => {
      form.setFieldValue('blockOverrides', {
        ...form.values.blockOverrides,
        [blockName]: {
          ...form.values.blockOverrides[blockName],
          ...changes,
        },
      })
    }

    const resetStyleValues = () => {
      if (!isBlock) return

      const color = script.originalFormatValue(selectedBlock, 'color') ?? ''

      const checkboxes = Object.fromEntries(
        StyleCheckboxData.map(({ id }) => [
          id,
          script.originalFormatValue(selectedBlock, id),
        ]),
      )

      setValues(selectedBlock, { color, ...checkboxes })
    }

    const resetScriptValues = () => {
      form.setFieldValue('fontCode', originalFontCode)
    }

    const isBlock = isFormatBlockName(selectedBlock)
    const [currentColor, pendingColor, originalColor] = gatherColors({
      block: selectedBlock,
      script,
      overrides: form.values.blockOverrides,
    })

    const hasStyleOverrides =
      isBlock &&
      (pendingColor !== originalColor ||
        // Check if any style property is overridden
        StyleCheckboxData.some(({ id }) => {
          const pendingValue = form.values.blockOverrides[selectedBlock]?.[id]
          const currentValue = script.currentFormatValue(selectedBlock, id)
          const value = pendingValue ?? currentValue
          const originalValue = script.originalFormatValue(selectedBlock, id)
          return value !== originalValue
        }))

    const hasFontOverride = form.values.fontCode !== originalFontCode
    const hasScriptOverride = hasFontOverride

    return (
      <ModalShell
        size={600}
        title="Customize script and block types"
        opened={controls.opened}
        onClose={controls.onClose}
        onConfirm={handleSubmit}
        confirmLabel="Save"
        cancelLabel="Cancel"
        errorMessage={controls.errorMessage}
        loading={controls.loading}
        disabled={!form.isValid() || !form.isDirty()}
      >
        <Group align="start" gap={0}>
          <Menu classNames={{ item: styles.menuItem }}>
            <Stack gap={5}>
              <FancyMenuItem
                iconClass="fas fa-tv-retro"
                title="Entire script"
                selected={!isBlock}
                onClick={() => {
                  setSelectedBlock('script')
                  setActiveTab('styles')
                }}
              />
              <Menu.Label>Block types</Menu.Label>
              <Stack gap={5}>
                {menuItems.map((item) => {
                  const isSelected = selectedBlock === item.node.name
                  return (
                    <FancyMenuItem
                      key={item.node.name}
                      customIcon={
                        <ElementIcon node={item.node} active={isSelected} />
                      }
                      title={item.displayName}
                      selected={isSelected}
                      onClick={() => {
                        if (!isFormatBlockName(item.node.name)) return
                        setSelectedBlock(item.node.name)
                      }}
                    />
                  )
                })}
              </Stack>
            </Stack>
          </Menu>
          <Divider orientation="vertical" ml={10} mr={20} />
          <Tabs
            classNames={{
              tab: styles.elementCustom_tab,
            }}
            value={activeTab}
            onChange={setActiveTab}
          >
            <Tabs.List>
              <Tabs.Tab value="layout" disabled={!isBlock}>
                Layout
              </Tabs.Tab>
              <Tabs.Tab value="styles">Styles</Tabs.Tab>
            </Tabs.List>
            <Tabs.Panel
              value="layout"
              className={styles.elementCustom_tabPanel}
              pb={20}
              pt={20}
              px={20}
            >
              {isBlock && (
                <LayoutInputs
                  script={script}
                  pendingOverrides={form.values.blockOverrides}
                  selectedBlock={selectedBlock}
                  blockTitle={findMenuItem(selectedBlock)?.displayName}
                  onChange={setValues}
                />
              )}
            </Tabs.Panel>
            <Tabs.Panel
              value="styles"
              className={styles.elementCustom_tabPanel}
              pb={20}
              pt={20}
              px={20}
            >
              {isBlock ? (
                <Stack
                  mt={20}
                  align="flex-start"
                  mih={273}
                  justify="space-between"
                >
                  <Group align="flex-start">
                    <TextColorPicker
                      pendingColor={pendingColor}
                      currentColor={currentColor}
                      originalColor={originalColor}
                      onChange={(newColor: string) => {
                        const color =
                          newColor.toLowerCase() === BLACKISH_TEXT
                            ? ''
                            : newColor

                        setValues(selectedBlock, { color })
                      }}
                      originalColors={script.originalFormatColors}
                    />
                    <FormattingCheckboxes
                      script={script}
                      pendingOverrides={form.values.blockOverrides}
                      selectedBlock={selectedBlock}
                      onChange={setValues}
                    />
                  </Group>
                  <Button
                    variant="outline"
                    disabled={!hasStyleOverrides}
                    onClick={resetStyleValues}
                  >
                    Reset{' '}
                    {findMenuItem(selectedBlock)?.displayName ?? "this block's"}{' '}
                    styles
                  </Button>
                </Stack>
              ) : (
                <Stack justify="space-between" mih={293}>
                  <FontPicker
                    fontCode={form.values.fontCode}
                    setFontCode={(code) => form.setFieldValue('fontCode', code)}
                    isOverridden={hasFontOverride}
                  />
                  <Button
                    variant="outline"
                    disabled={!hasScriptOverride}
                    onClick={resetScriptValues}
                    styles={{ root: { alignSelf: 'flex-start' } }}
                  >
                    Reset script-wide styles
                  </Button>
                </Stack>
              )}
            </Tabs.Panel>
          </Tabs>
        </Group>
        <Divider />
      </ModalShell>
    )
  },
)

export const showCustomizeStylesModal = (script: ILoadedScript) => {
  NiceModal.show(CustomizeStylesModal, { script })
}
